<template>
  <div class="w-full flex justify-between px-7.5 sticky left-0 bg-white">
    <ReviewTitle />
    <UserSettingsFloatingMenu v-slot="{ show, user }">
      <button
        class="font-semibold text-sm uppercase items-center flex flex-nowrap text-nowrap gap-4"
        @click.stop="show"
      >
        <p
          class="font-medium text-base rounded-full size-10 flex justify-center items-center bg-primary-50 text-primary-500"
        >
          {{ user?.firstName?.charAt(0) }}{{ user?.lastName?.charAt(0) }}
        </p>
      </button>
    </UserSettingsFloatingMenu>
  </div>

  <div class="w-full sticky left-0 bg-white">
    <Tabs v-model:tab="activeStep" :tabs="tabs">
      <template #tab-1="{ tab }">
        <button
          class="flex items-center px-6 py-5 w-full"
          @click="activeStep = ReviewStep.plan"
        >
          <div
            v-if="planErrors.length <= 0"
            :class="[
              'w-10 h-10 flex items-center justify-center shrink-0 rounded-full border-2 text-sm font-medium leading-4',
              tab.number === activeStep
                ? 'text-primary border-primary/60'
                : 'bg-white border-slate-300 text-slate-500',
            ]"
          >
            <CheckIcon class="w-6" />
          </div>
          <div
            v-else
            v-tooltip.bottom="planErrors.join('<br/>')"
            :class="[
              'w-10 h-10 flex items-center justify-center shrink-0 rounded-full border-2 font-medium leading-4 text-sm',
              tab.number === activeStep
                ? 'bg-white border-orange-600 text-orange-600'
                : '',
            ]"
          >
            0{{ tab.number }}
          </div>

          <div class="flex justify-between items-center w-full">
            <div class="flex items-start flex-col px-4">
              <div class="text-slate-600 font-medium leading-5">
                {{ tab.title }}
              </div>
              <div v-if="tab.subtitle" class="text-slate-400 text-sm leading-5">
                {{ tab.subtitle }}
              </div>
            </div>

            <PlanLockSwitch v-if="auth.user.value?.role === Role.ADMIN" />
          </div>
        </button>
      </template>

      <template #tab-2="{ tab }">
        <button
          class="flex items-center px-6 py-5 w-full"
          @click="activeStep = ReviewStep.execute"
        >
          <div
            v-if="executeErrors.length <= 0"
            :class="[
              'w-10 h-10 flex items-center justify-center shrink-0 rounded-full border-2 text-sm font-medium leading-4',
              tab.number === activeStep
                ? 'bg-white border-orange-600 text-orange-600'
                : '',
            ]"
          >
            <CheckIcon class="w-6" />
          </div>
          <div
            v-else
            v-tooltip.bottom="executeErrors.join('<br/>')"
            :class="[
              'w-10 h-10 flex items-center justify-center shrink-0 rounded-full border-2 font-medium leading-4 text-sm',
              tab.number === activeStep
                ? 'bg-white border-orange-600 text-orange-600'
                : '',
            ]"
          >
            0{{ tab.number }}
          </div>

          <div class="flex justify-between items-center w-full">
            <div class="flex items-start flex-col px-4">
              <div class="text-slate-600 font-medium leading-5">
                {{ tab.title }}
              </div>
              <div v-if="tab.subtitle" class="text-slate-400 text-sm leading-5">
                {{ tab.subtitle }}
              </div>
            </div>
          </div>
        </button>
      </template>

      <template #tab-3="{ tab }">
        <button
          class="flex items-center px-6 py-5 w-full"
          @click="activeStep = ReviewStep.export"
        >
          <div
            :class="[
              'w-10 h-10 flex items-center justify-center shrink-0 rounded-full border-2 font-medium leading-4 text-sm',
              tab.number === activeStep
                ? 'bg-white border-orange-600 text-orange-600'
                : '',
            ]"
          >
            0{{ tab.number }}
          </div>

          <div class="flex justify-between items-center w-full">
            <div class="flex items-start flex-col px-4">
              <div class="text-slate-600 font-medium leading-5">
                {{ tab.title }}
              </div>
              <div v-if="tab.subtitle" class="text-slate-400 text-sm leading-5">
                {{ tab.subtitle }}
              </div>
            </div>
          </div>
        </button>
      </template>
    </Tabs>
  </div>
  <router-view v-slot="{ Component }">
    <keep-alive>
      <component :is="Component" />
    </keep-alive>
  </router-view>
</template>

<script lang="ts" setup>
import { ReviewKey } from '@app/views/Review/use-review'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ReviewTitle from './Overview/ReviewTitle.vue'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import Tabs from '@app/components/Global/Tabs/Tabs.vue'
import { ReviewStep } from './review.types'
import { CheckIcon } from '@heroicons/vue/24/outline'
import { injectStrict } from '@app/utils/injectStrict'
import { AuthKey } from '@app/injectionKeys'
import { Role } from '@core/domain/types/role.type'
import PlanLockSwitch from './Plan/PlanLockSwitch.vue'
import UserSettingsFloatingMenu from '../Settings/UserSettings/UserSettingsFloatingMenu.vue'
import { RouteName } from '@app/router'

const router = useRouter()
const route = useRoute()
const review = injectStrict(ReviewKey)
const auth = injectStrict(AuthKey)
const lastPlanRoute = ref<string>(RouteName.REVIEW_PLAN_EVALUATORS)
const lastExecuteRoute = ref<string>(RouteName.REVIEW_EXECUTE_SCREENING)

watch(
  () => route.name as RouteName,
  (newRouteName) => {
    if (newRouteName.startsWith(RouteName.REVIEW_PLAN)) {
      lastPlanRoute.value = newRouteName
    } else if (newRouteName.startsWith(RouteName.REVIEW_EXECUTE)) {
      lastExecuteRoute.value = newRouteName
    }
  },
  { immediate: true },
)

const activeStep = computed({
  get: () => {
    if (route.path.includes('/plan')) {
      return ReviewStep.plan
    } else if (route.path.includes('/execute')) {
      return ReviewStep.execute
    } else if (route.path.includes('/export')) {
      return ReviewStep.export
    } else {
      return ReviewStep.plan
    }
  },
  set: (value: ReviewStep) => {
    if (value === ReviewStep.plan) {
      router.push({ name: lastPlanRoute.value })
    } else if (value === ReviewStep.execute) {
      router.push({ name: lastExecuteRoute.value })
    } else if (value === ReviewStep.export) {
      router.push({ name: 'Review - Export' })
    }
  },
})

const tabs = computed(() => {
  if (!review.entity.value) {
    return []
  }
  const tabs = [
    {
      number: ReviewStep.plan,
      title: 'Plan',
      subtitle: 'Outline objectives and criteria',
    },
    {
      number: ReviewStep.execute,
      title: 'Execute',
      subtitle: 'Perform screening and analysis',
    },
    {
      number: ReviewStep.export,
      title: 'Export',
      subtitle: 'Generate and share results',
    },
  ]

  return tabs
})

const planErrors = ref<string[]>([])
const executeErrors = ref<string[]>([])

const planValidationWorker = new Worker(
  new URL('./workers/planValidation.worker.ts', import.meta.url),
  {
    type: 'module',
  },
)

const executeValidationWorker = new Worker(
  new URL('./workers/executeValidation.worker.ts', import.meta.url),
  {
    type: 'module',
  },
)

watch(
  review.entity,
  () => {
    planValidationWorker.postMessage(JSON.stringify(review.entity.value))
    executeValidationWorker.postMessage(JSON.stringify(review.entity.value))
  },
  {
    immediate: true,
    deep: true,
  },
)
onMounted(() => {
  planValidationWorker.onmessage = (e) => {
    planErrors.value = JSON.parse(e.data)
  }
  executeValidationWorker.onmessage = (e) => {
    executeErrors.value = JSON.parse(e.data)
  }
  if (route.name === 'Review') {
    if (review.entity.value.plan?.lockState === ReviewLockState.LOCKED) {
      router.replace({ name: 'Review - Execute' })
    } else {
      router.replace({ name: 'Review - Plan' })
    }
  }
})

onUnmounted(() => {
  planValidationWorker.terminate()
  executeValidationWorker.terminate()
})
</script>

<style>
.ripple {
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

.ripple div:nth-child(2) {
  animation-delay: -0.5s;
}

@keyframes lds-ripple {
  0% {
    transform: scale(1);
    opacity: 0;
  }

  50% {
    transform: scale(0);
    opacity: 1;
  }

  100% {
    transform: scale(1);
    opacity: 0;
  }
}
</style>
